import React, { useState } from 'react';
import { Box, Typography, Chip } from '@mui/material';

/**
 * Reusable task button component with nameplate animation support.
 * Used in shop cards and theme configuration.
 *
 * @param {string} animationId - Profile effect folder name (e.g., 'starfall-tides')
 * @param {string} label - Task label text
 * @param {string} sublabel - Secondary text (e.g., 'Aktiivinen tehtävä')
 * @param {string} icon - Emoji icon for the task
 * @param {number} coins - Coin reward to display
 * @param {string} bgColor - Background color for the button
 * @param {string} textColor - Text color (default: white)
 * @param {boolean} staticMode - Show static image only (no animation)
 * @param {boolean} playOnHover - Play animation on hover (default: true)
 * @param {string} size - 'small' | 'medium' | 'large'
 * @param {string} animationAlign - 'center' | 'right' - alignment of animation background
 * @param {function} onClick - Click handler
 * @param {object} sx - Additional styles
 */
const TaskButtonComponent = ({
  animationId,
  label = 'Tehtävä',
  sublabel,
  icon = '🧹',
  coins,
  bgColor = '#87ceeb',
  textColor = '#fff',
  staticMode = false,
  playOnHover = true,
  size = 'medium',
  animationAlign = 'center',
  onClick,
  sx = {},
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imgError, setImgError] = useState(false);

  const sizeStyles = {
    small: { minHeight: 48, px: 1.5, py: 1, fontSize: '0.875rem', iconSize: '1rem', gap: 1, borderRadius: 1},
    medium: { minHeight: 64, px: 2, py: 1.5, fontSize: '1rem', iconSize: '1.5rem', gap: 1.5, borderRadius: 1},
    large: { minHeight: 80, px: 2.5, py: 2, fontSize: '1.125rem', iconSize: '2rem', gap: 2, borderRadius: 1},
  };

  const s = sizeStyles[size] || sizeStyles.medium;
  const showAnimation = animationId && !imgError && (staticMode ? false : (playOnHover ? isHovered : true));
  const staticSrc = animationId ? `/uploads/profile-effects/${animationId}/static.png` : null;
  const animatedSrc = animationId ? `/uploads/profile-effects/${animationId}/animated.png` : null;

  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => playOnHover && setIsHovered(true)}
      onMouseLeave={() => playOnHover && setIsHovered(false)}
      sx={{
        bgcolor: bgColor,
        color: textColor,
        borderRadius: 1,
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        display: 'flex',
        alignItems: 'center',
        gap: s.gap,
        minHeight: s.minHeight,
        px: s.px,
        py: s.py,
        transition: 'transform 0.15s',
        '&:hover': onClick ? { transform: 'scale(1.02)' } : {},
        ...sx,
      }}
    >
      {/* Animation/static background */}
      {animationId && !imgError && (
        <Box
          component="img"
          src={showAnimation ? animatedSrc : staticSrc}
          alt=""
          onError={() => setImgError(true)}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: animationAlign === 'right' ? 0 : 'auto',
            left: animationAlign === 'right' ? 'auto' : 0,
            width: animationAlign === 'right' ? 'auto' : '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: animationAlign === 'right' ? 'right center' : 'center',
            borderRadius: 1,
            opacity: 0.8,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 1,  display: 'flex', alignItems: 'center', gap: s.gap, flex: 1 }}>
        {icon && (
          <Typography sx={{ fontSize: s.iconSize, lineHeight: 1 }}>{icon}</Typography>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body1"
            fontWeight={600}
            sx={{ fontSize: s.fontSize, lineHeight: 1.2 }}
            noWrap
          >
            {label}
          </Typography>
          {sublabel && (
            <Typography variant="body2" sx={{ opacity: 0.85, fontSize: `calc(${s.fontSize} * 0.85)` }}>
              {sublabel}
            </Typography>
          )}
        </Box>
        {coins !== undefined && (
          <Chip
            label={`+${coins} 🪙`}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.25)',
              color: textColor,
              fontWeight: 600,
              fontSize: `calc(${s.fontSize} * 0.8)`,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default TaskButtonComponent;
